import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, settingsAPI } from '../api';
import { useGoogleLogin } from '@react-oauth/google';
import { HiGift, HiEye, HiEyeOff } from 'react-icons/hi';

const EMAIL_DOMAINS = ['@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com'];

/* ── Google "G" colour logo ─────────────────────────────── */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/* ── "or" divider ───────────────────────────────────────── */
function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--gray-200)' }} />
      <span style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: 'var(--gray-200)' }} />
    </div>
  );
}

export default function Register() {
  const [form, setForm]         = useState({ username: '', email: '', phone: '', password: '', referral_code: '' });
  const [phoneLocal, setPhoneLocal] = useState('');
  const [loading, setLoading]   = useState(false);
  const [gLoading, setGLoading] = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [settings, setSettings] = useState({});
  // Set-password step (shown after new Google registration)
  const [step, setStep]         = useState('form'); // 'form' | 'set-password'
  const [pwForm, setPwForm]     = useState({ password: '', confirm: '' });
  const [showNewPw, setShowNewPw]     = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [tosError, setTosError] = useState(false);
  const emailRef = useRef(null);
  const referralCodeRef = useRef('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      referralCodeRef.current = ref;
      setForm(f => ({ ...f, referral_code: ref }));
    }

    settingsAPI.public()
      .then(res => { if (res.data.success) setSettings(res.data.settings); })
      .catch(() => {});
  }, []);

  /* ── Quick-domain helper ────────────────────────────────── */
  const applyDomain = (domain) => {
    const atIdx = form.email.indexOf('@');
    const base  = atIdx !== -1 ? form.email.slice(0, atIdx) : form.email;
    const next  = base + domain;
    setForm(f => ({ ...f, email: next }));
    setTimeout(() => {
      if (emailRef.current) {
        emailRef.current.focus();
        emailRef.current.setSelectionRange(next.length, next.length);
      }
    }, 0);
  };

  /* ── Google sign-up ─────────────────────────────────────── */
  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (!agreedToTerms) { setTosError(true); return; }
      setTosError(false);
      setGLoading(true);
      try {
        const res = await authAPI.googleAuth({
          access_token: tokenResponse.access_token,
          referral_code: referralCodeRef.current || form.referral_code || undefined,
        });
        localStorage.setItem('vg_token', res.data.token);
        localStorage.setItem('vg_user', JSON.stringify(res.data.user));
        if (res.data.is_new_user) {
          const bonus = res.data.registration_bonus ?? settings.registration_bonus ?? 50;
          toast.success(`Account created! GHS ${bonus} bonus added 🎉`);
          setStep('set-password'); // prompt new Google users to set a password
        } else {
          toast.success('Signed in with Google!');
          navigate('/');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Google sign-up failed');
      } finally {
        setGLoading(false);
      }
    },
    onError: () => toast.error('Google sign-up was cancelled'),
  });

  /* ── Set password (Google new-user step) ────────────────── */
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (pwForm.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (pwForm.password !== pwForm.confirm) return toast.error('Passwords do not match');
    setPwLoading(true);
    try {
      await authAPI.setPassword({ password: pwForm.password });
      toast.success('Password set! You can now also log in with your email.');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set password');
    } finally {
      setPwLoading(false);
    }
  };

  /* ── Regular registration ───────────────────────────────── */
  const handle = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setTosError(true); return; }
    setTosError(false);
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const payload = {
        ...form,
        phone: phoneLocal ? '+233' + phoneLocal.replace(/^0+/, '') : '',
      };
      const res = await authAPI.register(payload);
      localStorage.setItem('vg_token', res.data.token);
      toast.success(`Registration successful! GHS ${settings.registration_bonus ?? 50} bonus added`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || gLoading;

  /* ── Set-password step (new Google users) ────────────────── */
  if (step === 'set-password') {
    return (
      <div className="auth-page">
        <div className="auth-header">
          <div className="auth-logo">Vest<span>-Grow</span></div>
          <div className="auth-tagline">One last step — secure your account</div>
        </div>

        <div className="auth-body">
          <h2 style={{ marginBottom: 6, fontFamily: 'var(--font-display)', fontSize: 24 }}>
            Set a Password
          </h2>
          <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20 }}>
            Add a password so you can also sign in with your email later.
          </p>

          <form onSubmit={handleSetPassword}>
            {/* Password */}
            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  type={showNewPw ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  value={pwForm.password}
                  onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))}
                  style={{ paddingRight: 44 }}
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw(v => !v)}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--gray-400)', display: 'flex', alignItems: 'center', padding: 0,
                  }}
                >
                  {showNewPw ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  type={showConfirmPw ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  style={{ paddingRight: 44 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPw(v => !v)}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--gray-400)', display: 'flex', alignItems: 'center', padding: 0,
                  }}
                >
                  {showConfirmPw ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={pwLoading}>
              {pwLoading ? 'Setting password…' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-header">
        <div className="auth-logo">Vest<span>-Grow</span></div>
        <div className="auth-tagline">
          {settings.registration_bonus
            ? `Get GHS ${settings.registration_bonus} on sign-up!`
            : 'Join thousands of investors earning daily'}
        </div>
      </div>

      <div className="auth-body">
        <h2 style={{ marginBottom: 6, fontFamily: 'var(--font-display)', fontSize: 24 }}>Create Account</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20 }}>
          {settings.registration_bonus ? `Get GHS ${settings.registration_bonus} registration bonus!` : 'Create your account to get started!'}
        </p>

        <div className="alert alert-gold" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <HiGift size={18} style={{ flexShrink: 0 }} />
          Registration Bonus: <strong>GHS {settings.registration_bonus ?? '…'}</strong>
          {settings.daily_checkin_bonus && <> | Daily Check-in: <strong>GHS {settings.daily_checkin_bonus}</strong></>}
        </div>

        {/* ── Google Sign-Up button ──────────────────────────── */}
        <button
          type="button"
          onClick={() => {
            if (!agreedToTerms) { setTosError(true); return; }
            referralCodeRef.current = form.referral_code || referralCodeRef.current;
            googleSignup();
          }}
          disabled={busy}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '12px 16px',
            background: 'var(--white)',
            border: '1.5px solid var(--gray-200)',
            borderRadius: 'var(--radius)',
            fontSize: 14, fontWeight: 600,
            color: 'var(--gray-800)',
            cursor: busy ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
            opacity: gLoading ? 0.7 : 1,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
          onMouseEnter={e => {
            if (!busy) {
              e.currentTarget.style.borderColor = 'var(--blue-mid)';
              e.currentTarget.style.background = 'var(--blue-pale)';
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--gray-200)';
            e.currentTarget.style.background = 'var(--white)';
          }}
        >
          {gLoading
            ? <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Signing up with Google…</span>
            : <><GoogleIcon /> Sign up with Google</>}
        </button>

        <Divider label="or sign up with email" />

        <form onSubmit={handle}>

          {/* Username */}
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              className="form-control"
              type="text"
              placeholder="e.g. john123"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              required
            />
          </div>

          {/* Email + quick-domain buttons */}
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              ref={emailRef}
              className="form-control"
              type="email"
              placeholder="Type email here"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {EMAIL_DOMAINS.map(domain => (
                <button
                  key={domain}
                  type="button"
                  onClick={() => applyDomain(domain)}
                  style={{
                    fontSize: 12, fontWeight: 600,
                    padding: '5px 10px', borderRadius: 7,
                    border: '1.5px solid',
                    borderColor: form.email.endsWith(domain) ? 'var(--blue-mid)' : 'var(--gray-200)',
                    background:  form.email.endsWith(domain) ? 'var(--blue-pale)' : 'var(--white)',
                    color:       form.email.endsWith(domain) ? 'var(--blue-mid)' : 'var(--gray-600)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {domain}
                </button>
              ))}
            </div>
          </div>

          {/* Phone with +233 prefix */}
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <div style={{ display: 'flex' }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                padding: '0 14px',
                background: 'var(--gray-100)',
                border: '1.5px solid var(--gray-200)',
                borderRight: 'none',
                borderRadius: 'var(--radius) 0 0 var(--radius)',
                fontSize: 14, fontWeight: 700,
                color: 'var(--blue-mid)',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                +233
              </div>
              <input
                className="form-control"
                type="tel"
                placeholder="XX XXX XXXX"
                value={phoneLocal}
                onChange={e => setPhoneLocal(e.target.value.replace(/[^\d\s]/g, ''))}
                style={{ borderRadius: '0 var(--radius) var(--radius) 0', flex: 1 }}
                maxLength={12}
              />
            </div>
            <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 5 }}>
              Enter your number without the leading 0 — e.g. 24 123 4567
            </p>
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-control"
                type={showPw ? 'text' : 'password'}
                placeholder="Min 6 characters"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                style={{ paddingRight: 44 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--gray-400)', display: 'flex', alignItems: 'center', padding: 0,
                }}
              >
                {showPw ? <HiEyeOff size={20} /> : <HiEye size={20} />}
              </button>
            </div>
          </div>

          {/* Referral Code */}
          <div className="form-group">
            <label className="form-label">
              Referral Code{' '}
              <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(Optional)</span>
            </label>
            <input
              className="form-control"
              type="text"
              placeholder="Enter referral code"
              value={form.referral_code}
              onChange={e => setForm({ ...form, referral_code: e.target.value })}
            />
          </div>

          {/* ── Terms of Service checkbox ──────────────────── */}
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              cursor: 'pointer', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked) setTosError(false);
                }}
                style={{
                  marginTop: 2, flexShrink: 0,
                  width: 17, height: 17, cursor: 'pointer',
                  accentColor: 'var(--blue-mid)',
                }}
              />
              <span style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.5 }}>
                I have read and agree to the{' '}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--blue-mid)', fontWeight: 600, textDecoration: 'underline' }}
                  onClick={e => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--blue-mid)', fontWeight: 600, textDecoration: 'underline' }}
                  onClick={e => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            {tosError && (
              <div style={{
                marginTop: 8,
                display: 'flex', alignItems: 'flex-start', gap: 8,
                background: '#fff4f4',
                border: '1.5px solid #f5c2c2',
                borderRadius: 9,
                padding: '10px 12px',
                fontSize: 13,
                color: '#c0392b',
                lineHeight: 1.5,
              }}>
                <span>
                  Please read and check the box above to agree to our Terms of Service
                  before creating your account.
                </span>
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--gray-600)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--blue-mid)', fontWeight: 600 }}>Login</Link>
        </p>

        {/* ── Footer links ───────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 24,
          marginTop: 20, paddingTop: 16,
          borderTop: '1px solid var(--gray-200)',
        }}>
          <Link to="/about" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none', fontWeight: 500 }}>
            About Us
          </Link>
          <Link to="/privacy-policy" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none', fontWeight: 500 }}>
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}